import React, { useState } from "react";
import { Card, CardBody, Badge, Progress } from "reactstrap";
import { formatCurrency } from "../utils/dreCalculations";
import { MONTH_LABELS } from "../constants";

/**
 * DRETable
 * Tabela DRE no formato da imagem de referência:
 *   CATEGORIA | PERCENTUAL (barra) | VALOR
 * Seções expansíveis: Receitas Operacionais, Despesas e Custos,
 * com totalizadores e resultado final.
 */
const DRETable = ({ dreData, year, month }) => {
    const { income, expense, totals } = dreData;
    const [expandedIncome, setExpandedIncome] = useState(true);
    const [expandedExpense, setExpandedExpense] = useState(true);

    const monthLabel = MONTH_LABELS[(month || 1) - 1];
    const resultIsPositive = totals.netResult >= 0;

    return (
        <Card className="border-0 shadow-sm">
            <CardBody className="p-0">

                {/* Título do card */}
                <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid #e9ecef" }}>
                    <h6 className="mb-0 fw-bold text-uppercase" style={{ color: "#37474f", fontSize: "0.85rem", letterSpacing: "0.5px" }}>
                        DRE Gerencial – {monthLabel} / {year}
                    </h6>
                </div>

                {/* Cabeçalho da tabela */}
                <div
                    className="d-flex align-items-center px-4 py-2"
                    style={{ background: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}
                >
                    <div style={{ flex: 1 }}>
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#78909c" }}>
                            Categoria
                        </span>
                    </div>
                    <div style={{ width: "30%", textAlign: "center" }}>
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#78909c" }}>
                            Percentual
                        </span>
                    </div>
                    <div style={{ width: "160px", textAlign: "right" }}>
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#78909c" }}>
                            Valor
                        </span>
                    </div>
                </div>

                {/* ═══ RECEITAS OPERACIONAIS ═══ */}
                <div
                    className="d-flex align-items-center px-4 py-2"
                    style={{ cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                    onClick={() => setExpandedIncome(!expandedIncome)}
                >
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                        <i
                            className={`mdi mdi-plus-circle${expandedIncome ? "-outline" : ""}`}
                            style={{ color: "#2e7d32", fontSize: "1rem" }}
                        />
                        <span className="fw-bold text-uppercase" style={{ color: "#2e7d32", fontSize: "0.8rem", letterSpacing: "0.3px" }}>
                            Receitas Operacionais
                        </span>
                    </div>
                    <div style={{ width: "30%" }} />
                    <div style={{ width: "160px" }} />
                </div>

                {expandedIncome && income.map((line) => {
                    const pct = totals.totalIncome > 0 ? (line.grossAmount / totals.totalIncome * 100) : 0;
                    return (
                        <div
                            key={line.id}
                            className="d-flex align-items-center px-4 py-2"
                            style={{ borderBottom: "1px solid #f5f5f5", paddingLeft: "48px !important" }}
                        >
                            <div style={{ flex: 1, paddingLeft: "28px" }}>
                                <span style={{ fontSize: "0.85rem", color: "#37474f" }}>
                                    {line.name}
                                </span>
                            </div>
                            <div style={{ width: "30%", display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                                <span style={{ fontSize: "0.75rem", color: "#78909c", minWidth: "40px", textAlign: "right" }}>
                                    {pct.toFixed(1)}%
                                </span>
                                <div style={{ flex: 1, maxWidth: "180px" }}>
                                    <Progress
                                        value={pct}
                                        color="success"
                                        style={{ height: "6px", borderRadius: "3px", backgroundColor: "#e8f5e9" }}
                                    />
                                </div>
                            </div>
                            <div style={{ width: "160px", textAlign: "right" }}>
                                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#37474f" }}>
                                    {line.grossAmount > 0 ? formatCurrency(line.grossAmount) : "—"}
                                </span>
                            </div>
                        </div>
                    );
                })}

                {/* TOTAL RECEITAS */}
                <div
                    className="d-flex align-items-center px-4 py-2"
                    style={{ borderBottom: "2px solid #e9ecef", background: "#fafafa" }}
                >
                    <div style={{ flex: 1, paddingLeft: "28px" }}>
                        <span className="fw-bold text-uppercase" style={{ fontSize: "0.8rem", color: "#37474f" }}>
                            Total Receitas
                        </span>
                    </div>
                    <div style={{ width: "30%" }} />
                    <div style={{ width: "160px", textAlign: "right" }}>
                        <span className="fw-bold" style={{ fontSize: "0.9rem", color: "#2e7d32" }}>
                            {formatCurrency(totals.totalIncome)}
                        </span>
                    </div>
                </div>

                {/* ═══ DESPESAS E CUSTOS ═══ */}
                <div
                    className="d-flex align-items-center px-4 py-2"
                    style={{ cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                    onClick={() => setExpandedExpense(!expandedExpense)}
                >
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                        <i
                            className={`mdi mdi-minus-circle${expandedExpense ? "-outline" : ""}`}
                            style={{ color: "#c62828", fontSize: "1rem" }}
                        />
                        <span className="fw-bold text-uppercase" style={{ color: "#c62828", fontSize: "0.8rem", letterSpacing: "0.3px" }}>
                            Despesas e Custos
                        </span>
                    </div>
                    <div style={{ width: "30%" }} />
                    <div style={{ width: "160px" }} />
                </div>

                {expandedExpense && expense.map((line) => {
                    // % da despesa em relação à receita total (impacto no DRE)
                    const pctOfIncome = totals.totalIncome > 0 ? (line.grossAmount / totals.totalIncome * 100) : 0;
                    return (
                        <div
                            key={line.id}
                            className="d-flex align-items-center px-4 py-2"
                            style={{ borderBottom: "1px solid #f5f5f5" }}
                        >
                            <div style={{ flex: 1, paddingLeft: "28px" }}>
                                <span style={{ fontSize: "0.85rem", color: "#37474f" }}>
                                    {line.name}
                                </span>
                            </div>
                            <div style={{ width: "30%", display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                                <span style={{ fontSize: "0.75rem", color: "#78909c", minWidth: "40px", textAlign: "right" }}>
                                    {pctOfIncome.toFixed(1)}%
                                </span>
                                <div style={{ flex: 1, maxWidth: "180px" }}>
                                    <Progress
                                        value={Math.min(pctOfIncome, 100)}
                                        color="danger"
                                        style={{ height: "6px", borderRadius: "3px", backgroundColor: "#ffebee" }}
                                    />
                                </div>
                            </div>
                            <div style={{ width: "160px", textAlign: "right" }}>
                                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#c62828" }}>
                                    {line.grossAmount > 0 ? formatCurrency(line.grossAmount) : "—"}
                                </span>
                            </div>
                        </div>
                    );
                })}

                {/* TOTAL DESPESAS */}
                <div
                    className="d-flex align-items-center px-4 py-2"
                    style={{ borderBottom: "2px solid #e9ecef", background: "#fafafa" }}
                >
                    <div style={{ flex: 1, paddingLeft: "28px" }}>
                        <span className="fw-bold text-uppercase" style={{ fontSize: "0.8rem", color: "#37474f" }}>
                            Total Despesas
                        </span>
                    </div>
                    <div style={{ width: "30%" }} />
                    <div style={{ width: "160px", textAlign: "right" }}>
                        <span className="fw-bold" style={{ fontSize: "0.9rem", color: "#c62828" }}>
                            {formatCurrency(totals.totalExpense)}
                        </span>
                    </div>
                </div>

                {/* ═══ RESULTADO LÍQUIDO DO EXERCÍCIO ═══ */}
                <div
                    className="d-flex align-items-center px-4 py-3 mx-3 my-3"
                    style={{
                        borderRadius: "8px",
                        border: `2px solid ${resultIsPositive ? "#2e7d32" : "#c62828"}`,
                        background: resultIsPositive ? "#f1f8e9" : "#fce4ec",
                    }}
                >
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px" }}>
                        <span className="fw-bold text-uppercase" style={{
                            fontSize: "0.85rem",
                            color: "#37474f",
                            letterSpacing: "0.3px"
                        }}>
                            Resultado Líquido do Exercício
                        </span>
                        <Badge
                            style={{
                                background: resultIsPositive ? "#e8f5e9" : "#ffebee",
                                color: resultIsPositive ? "#2e7d32" : "#c62828",
                                border: `1px solid ${resultIsPositive ? "#c8e6c9" : "#ffcdd2"}`,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                padding: "3px 8px",
                            }}
                        >
                            Margem: {totals.margin.toFixed(1)}%
                        </Badge>
                    </div>
                    <div style={{ width: "160px", textAlign: "right" }}>
                        <span className="fw-bold" style={{
                            fontSize: "1.1rem",
                            color: resultIsPositive ? "#2e7d32" : "#c62828",
                        }}>
                            {formatCurrency(totals.netResult)}
                        </span>
                    </div>
                </div>

            </CardBody>
        </Card>
    );
};

export default DRETable;
